/* eslint-disable import/no-extraneous-dependencies */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AppConfigSettings,
  GeneralErrorAction,
  GeneralSuccessAction,
  UserNotificationAction,
} from '@arc/arc-shared-frontend';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import moment from 'moment';
import { MessageService } from '@arc/aureus';
import {
  BankHoliday,
  ConfirmBankAuthorizationVerbiagePayload,
  getAuthorizePaymentErrorMessage,
  getTransactionsWithUpdatedStatuses,
  LoadingSpinnerService,
  PaymentAccountsServiceFactory,
  PaymentAuthorizationVerbiageServiceFactory,
  PaymentDateHelperService,
  PaymentExecutionV1Response,
  PaymentInformationServiceFactory,
  PaymentSchedulerServiceFactory,
  PaymentService,
  PaymentStatus,
  SerializableMap,
  SessionPaymentTransaction,
  TransactionAuthorizationResult,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  AcceptSurchargeAuthorizationVerbiageCompleteAction,
  AcceptSurchargeAuthorizationVerbiageErrorAction,
  AuthorizePaymentCompleteAction,
  AuthorizePaymentErrorAction,
  CheckPaymentStatusCompleteAction,
  CheckPaymentStatusErrorAction,
  PaymentActionsUnion,
  RetrieveBankAuthorizationVerbiageCompleteAction,
  RetrieveSurchargeAuthorizationVerbiageCompleteAction,
  UpdatePaymentAccountExpirationSuccessAction,
} from '../actions/btpay.actions';
import {
  StepChangeAnalyticsAction,
  TrackAnalyticsAction,
} from '../actions/btpay-analytics.actions';
import { PayActions } from '../actions/index';
import { CutOffTimeStore, PaymentStoreState } from '../reducers/btpay.reducers';
import { PaymentRuleStore } from '../../payment-rules';
import { SessionStore } from '../../session-store/reducers/session.reducer';
import { UpdateSessionPaymentTransactionStatusAction } from '../../session-store/actions/session.actions';
import {
  selectTransactions,
  selectVirtualOtherwisePrimaryTenantId,
} from '../../session-store/selectors/session.selectors';
import {
  selectBankHolidayDates,
  selectBuyerAccountIdentifiers,
  selectIsScheduledPayment,
  selectIsScheduledTransaction,
  selectPaymentReferenceNumber,
  selectPaymentStatusCheckCount,
  selectTransactionAuthorizationResults,
} from '../selectors/btpay.selectors';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class PayEffects {
  private checkPaymentStatusInterval = +this.appConfig.getconfig(
    'checkPaymentStatusInterval',
  );

  private checkPaymentStatusTimeout = +this.appConfig.getconfig(
    'checkPaymentStatusTimeout',
  );

  constructor(
    private actions$: Actions<PaymentActionsUnion>,
    private paymentAuthorizationServiceFactory: PaymentAuthorizationVerbiageServiceFactory,
    private paymentInformationServiceFactory: PaymentInformationServiceFactory,
    private paymentService: PaymentService,
    private store$: Store<PaymentStoreState>,
    private translationService: TranslateService,
    private messageService: MessageService,
    private paymentAccountServiceFactory: PaymentAccountsServiceFactory,
    private paymentSchedulerServiceFactory: PaymentSchedulerServiceFactory,
    private loadingSpinnerService: LoadingSpinnerService,
    private paymentDateHelperService: PaymentDateHelperService,
    private appConfig: AppConfigSettings,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  updatePaymentAccountExpirationAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.UpdatePaymentAccountExpirationAction),
      exhaustMap((action) =>
        this.paymentAccountServiceFactory
          .create()
          .updateExpirationDateV1(
            action.payload.tenantId,
            action.payload.paymentAccountBurn,
            action.payload.requestBody,
          )
          .pipe(
            map(() => UpdatePaymentAccountExpirationSuccessAction()),
            catchError((error: HttpErrorResponse) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              let errorMessage = (error.error?.error ?? error.error) as string;

              if (Array.isArray(error.error)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                errorMessage = (error.error[0]?.message ??
                  error.error[0]) as string;
              }

              return of(
                PayActions.UpdatePaymentAccountExpirationFailureAction(),
                new UserNotificationAction({
                  type: 'error',
                  message: errorMessage,
                }),
              );
            }),
          ),
      ),
    ),
  );

  getBankHolidaysEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.GetBankHolidaysAction),
      exhaustMap((action) =>
        this.paymentSchedulerServiceFactory
          .create()
          .getBankHolidayScheduleV1(
            action.payload.tenantId,
            action.payload.country,
          )
          .pipe(
            map((response) => {
              const bankHolidays = response.holidayDates.map<BankHoliday>(
                (d) => ({
                  name: d.holidayName,
                  date: new Date(d.year, d.month - 1, d.day),
                }),
              );
              return PayActions.GetBankHolidaysSuccessAction({ bankHolidays });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                new GeneralErrorAction({
                  actionType: PayActions.GetBankHolidaysAction.type,
                  errorObj: error,
                }),
              );
            }),
          ),
      ),
    ),
  );

  retrieveBankAuthorizationVerbiage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.RetrieveBankAuthorizationVerbiageAction.type),
      map((action) => action.payload),
      concatLatestFrom(() => this.store$.select(PaymentRuleStore)),
      exhaustMap(([paymentAccountPayload, ,]) => {
        return this.paymentAuthorizationServiceFactory
          .create()
          .retrieveAuthorizationVerbiageV1(
            paymentAccountPayload.tenantId,
            paymentAccountPayload.request,
          )
          .pipe(
            map((response) => {
              return RetrieveBankAuthorizationVerbiageCompleteAction({
                payload: response,
              });
            }),
            catchError(() => {
              return of(
                RetrieveBankAuthorizationVerbiageCompleteAction({
                  payload: null,
                }),
              );
            }),
          );
      }),
    ),
  );

  acceptBankAuthorizationVerbiage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.AcceptBankAuthorizationVerbiageAction.type),
      map((action) => action.payload),
      exhaustMap((payload: ConfirmBankAuthorizationVerbiagePayload) => {
        return this.paymentAuthorizationServiceFactory
          .create()
          .acceptAuthorizationVerbiageV1(payload.tenantId, payload.request)
          .pipe(
            map(() => {
              return new GeneralSuccessAction(
                PayActions.AcceptBankAuthorizationVerbiageAction.type,
              );
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                new GeneralErrorAction({
                  actionType:
                    PayActions.AcceptBankAuthorizationVerbiageAction.type,
                  errorObj: error,
                }),
              );
            }),
          );
      }),
    ),
  );

  retrieveSurchargeAuthorizationVerbiage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.RetrieveSurchargeAuthorizationVerbiageAction),
      map((action) => action.payload),
      concatLatestFrom(() => this.store$.select(PaymentRuleStore)),
      exhaustMap(([paymentAccountPayload, paymentRules]) => {
        return this.paymentAuthorizationServiceFactory
          .create()
          .retrieveAuthorizationVerbiageV1(
            paymentAccountPayload.tenantId,
            paymentAccountPayload.request,
          )
          .pipe(
            map((response) => {
              return RetrieveSurchargeAuthorizationVerbiageCompleteAction({
                payload: response,
              });
            }),
            catchError(() => {
              const authorizationTextKey = paymentRules.settings.payments
                .acceptBank
                ? 'ARC_PAYMENT_SURCHARGE_DEFAULT_VERBIAGE_WITH_ACH'
                : 'ARC_PAYMENT_SURCHARGE_DEFAULT_VERBIAGE';
              const authorizationText = this.translationService.instant(
                authorizationTextKey,
              ) as string;

              return of(
                RetrieveSurchargeAuthorizationVerbiageCompleteAction({
                  payload: {
                    agreementText: this.translationService.instant(
                      'ARC_PAYMENT_SURCHARGE_DEFAULT_VERBIAGE_AGREEMENT_TEXT',
                    ) as string,
                    authorizationText,
                    language: 'en',
                    errors: null,
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  acceptSurchargeAuthorizationVerbiage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.AcceptSurchargeAuthorizationVerbiageAction.type),
      map((action) => action.payload),
      exhaustMap((paymentAccountPayload) =>
        this.paymentAuthorizationServiceFactory
          .create()
          .acceptAuthorizationVerbiageV1(
            paymentAccountPayload.tenantId,
            paymentAccountPayload.request,
          )
          .pipe(
            map((response) =>
              AcceptSurchargeAuthorizationVerbiageCompleteAction({
                payload: response,
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                AcceptSurchargeAuthorizationVerbiageErrorAction({
                  payload: error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  authorizePaymentComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.AuthorizePaymentCompleteAction),
      withLatestFrom(this.store$.select(selectTransactions)),
      exhaustMap(([action, sessionTransactions]) => {
        const transactionAuthorizationResultMap: SerializableMap<TransactionAuthorizationResult> =
          {};
        transactionAuthorizationResultMap[action.transactionReference] =
          action.transactionAuthorizationResult;

        const updatedSessionTransactions: SessionPaymentTransaction[] =
          getTransactionsWithUpdatedStatuses(
            transactionAuthorizationResultMap,
            sessionTransactions,
          );

        return of(
          UpdateSessionPaymentTransactionStatusAction({
            payload: updatedSessionTransactions,
          }),
        );
      }),
    ),
  );

  authorizePaymentError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.AuthorizePaymentErrorAction),
      withLatestFrom(this.store$.select(selectTransactions)),
      exhaustMap(([action, sessionTransactions]) => {
        const transactionAuthorizationResultMap: SerializableMap<TransactionAuthorizationResult> =
          {};
        transactionAuthorizationResultMap[action.transactionReference] =
          action.payload;
        const updatedSessionTransactions: SessionPaymentTransaction[] =
          getTransactionsWithUpdatedStatuses(
            transactionAuthorizationResultMap,
            sessionTransactions,
          );

        return of(
          UpdateSessionPaymentTransactionStatusAction({
            payload: updatedSessionTransactions,
          }),
        );
      }),
    ),
  );

  checkPaymentStatusComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PayActions.CheckPaymentStatusCompleteAction,
        PayActions.CheckPaymentStatusCancelledAction,
      ),
      withLatestFrom(this.store$.select(selectTransactions)),
      exhaustMap(([action, sessionTransactions]) => {
        const updatedSessionTransactions: SessionPaymentTransaction[] =
          getTransactionsWithUpdatedStatuses(
            action.results,
            sessionTransactions,
          );
        return of(
          UpdateSessionPaymentTransactionStatusAction({
            payload: updatedSessionTransactions,
          }),
        );
      }),
    ),
  );

  authorizePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.AuthorizePaymentAction.type),
      map((action) => action.payload),
      concatLatestFrom(() => [
        this.store$.select(selectPaymentReferenceNumber),
        this.store$.select(selectIsScheduledTransaction),
        this.store$.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      mergeMap(
        ([payload, paymentReference, isScheduledTransaction, tenantId]) => {
          return this.paymentInformationServiceFactory
            .create()
            .executePaymentV1(
              tenantId,
              paymentReference,
              payload.transactionReference,
              null,
            )
            .pipe(
              map((response: PaymentExecutionV1Response) => {
                return AuthorizePaymentCompleteAction({
                  payload: response,
                  transactionAuthorizationResult: {
                    gpalStatus: response.status as PaymentStatus,
                    paymentDate: response.paymentDate,
                    isScheduledTransaction: isScheduledTransaction,
                  },
                  transactionReference: payload.transactionReference,
                });
              }),
              catchError((error: HttpErrorResponse) => {
                let gpalStatus = PaymentStatus.FAILED;
                let errorMessage = null;

                if (error instanceof HttpErrorResponse) {
                  errorMessage = getAuthorizePaymentErrorMessage(error);
                  if (error.status >= 500) {
                    gpalStatus = PaymentStatus.TIMEOUT;
                  }
                }

                return of(
                  AuthorizePaymentErrorAction({
                    payload: {
                      gpalStatus,
                      errorMessage: errorMessage as string,
                      isScheduledTransaction: isScheduledTransaction,
                    },
                    transactionReference: payload.transactionReference,
                  }),
                );
              }),
            );
        },
      ),
    ),
  );

  checkPaymentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.CheckPaymentStatusAction.type),
      map((action) => action.payload),
      concatLatestFrom(() => [
        this.store$.pipe(select(selectIsScheduledPayment)),
        this.store$.pipe(select(selectPaymentStatusCheckCount)),
      ]),
      exhaustMap(([payload, isPaymentScheduled, statusCheckCount]) => {
        return this.paymentService
          .onCheckStatus(
            payload.tenantId,
            payload.masterConfirmationNumber,
            isPaymentScheduled,
          )
          .pipe(
            map((results) => {
              const authorizationPending = Object.values(results).some(
                (value) => value.gpalStatus === PaymentStatus.IN_PROGRESS,
              );
              const checkStatusCount =
                statusCheckCount + this.checkPaymentStatusInterval;
              const checkStatusTimeout =
                checkStatusCount > this.checkPaymentStatusTimeout &&
                authorizationPending;

              return CheckPaymentStatusCompleteAction({
                results,
                checkStatusCount,
                checkStatusTimeout,
                authorizationPending,
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                CheckPaymentStatusErrorAction({
                  payload: error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  cancelCheckPaymentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.CancelCheckPaymentStatusAction.type),
      concatLatestFrom(() => [
        this.store$.pipe(select(SessionStore)),
        this.store$.pipe(select(selectTransactionAuthorizationResults)),
      ]),
      exhaustMap(([, session, transactionAuthorizationResults]) => {
        const results: SerializableMap<TransactionAuthorizationResult> = {};
        const paymentDate = moment().utc().format();

        // eslint-disable-next-line guard-for-in
        for (const transaction of session.payment.transactions) {
          const result: TransactionAuthorizationResult = {
            gpalStatus: PaymentStatus.IN_PROGRESS,
            paymentDate,
            isScheduledTransaction:
              transactionAuthorizationResults[transaction.transactionReference]
                .isScheduledTransaction,
          };

          results[transaction.transactionReference] = result;
        }

        return of(
          PayActions.CheckPaymentStatusCancelledAction({
            results,
          }),
        );
      }),
    ),
  );

  stepChangeAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StepChangeAnalyticsAction),
      map((action) => action.payload),
      exhaustMap((payload) =>
        of(
          TrackAnalyticsAction({
            eventKey: 'BTPAY_CHECKOUT_STEP_CHANGED',
            eventData: payload,
          }),
        ),
      ),
    ),
  );

  getCutoffTimeSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.GetCutoffTimeForAchAction),
      exhaustMap((action) =>
        this.paymentSchedulerServiceFactory
          .create()
          .getCutoffTimeScheduleV1(
            action.payload.tenantId,
            action.payload.version,
          )
          .pipe(
            map((response) => {
              return PayActions.GetCutoffTimeForAchSuccessAction({
                paymentCutoffTime: new Date(response),
              });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                new GeneralErrorAction({
                  actionType: PayActions.GetCutoffTimeForAchAction.type,
                  errorObj: error,
                }),
              );
            }),
          ),
      ),
    ),
  );

  getAutopayStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.GetAutopayStatusAction),
      concatLatestFrom(() => [
        this.store$.select(selectVirtualOtherwisePrimaryTenantId),
        this.store$.select(selectBuyerAccountIdentifiers),
      ]),
      exhaustMap(([, tenantId, buyerAccounts]) => {
        const buyerAccount = buyerAccounts[0].split(':')[1];
        return this.paymentSchedulerServiceFactory
          .create()
          .getAutopayStatusV1(tenantId, buyerAccount)
          .pipe(
            map((autopayStatusResponse) =>
              PayActions.GetAutopayStatusSuccessAction({
                autopayStatusResponse,
              }),
            ),
            catchError(() => of(PayActions.GetAutopayStatusErrorAction())),
          );
      }),
    ),
  );

  /* istanbul ignore next */
  loadUntilGetAutopayStatusComplete$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [PayActions.GetAutopayStatusAction.type],
        [
          PayActions.GetAutopayStatusSuccessAction.type,
          PayActions.GetAutopayStatusErrorAction.type,
        ],
      ),
    { dispatch: false },
  );

  /* istanbul ignore next */
  loadUntilSurchargeVerbiageRetrieved$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [PayActions.RetrieveSurchargeAuthorizationVerbiageAction.type],
        [PayActions.RetrieveSurchargeAuthorizationVerbiageCompleteAction.type],
      ),
    { dispatch: false },
  );

  validatePaymentDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayActions.ValidatePaymentDateAction),
      concatLatestFrom(() => [
        this.store$.select(selectBankHolidayDates),
        this.store$.select(CutOffTimeStore),
      ]),
      exhaustMap(([action, bankholidayDates, cutOffTime]) =>
        this.paymentDateHelperService
          .validatePaymentDate(
            action.originalPaymentDate,
            action.selectedPaymentMethodType,
            bankholidayDates,
            cutOffTime,
          )
          .pipe(
            map((paymentDateValidationResult) =>
              PayActions.ValidatePaymentDateCompleteAction({
                paymentDateValidationResult,
              }),
            ),
          ),
      ),
    ),
  );

  /* istanbul ignore next */
  private loadUntilActionsComplete(
    sourceActionTypes: string[],
    completeActionTypes: string[],
  ) {
    return this.actions$.pipe(
      ofType(...sourceActionTypes),
      exhaustMap(() => {
        const completeActions$ = this.actions$.pipe(
          ofType(...completeActionTypes),
          take(1),
        );

        this.loadingSpinnerService.loadUntil(completeActions$);

        return of();
      }),
    );
  }
}
